import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { OpenBankingModalState, useLazyOpenBankingConsentUrl, useOpenBankingConsentState } from 'src/hooks/openBanking';
import { IncomeReviewFieldsType, Period } from '@brighte/validation';
import { OpenBankingFeedbackModal, OpenBankingInstructionModal } from '@components/OpenBanking';
import { datadogRum } from '@datadog/browser-rum';
import { OpenBankingFeedBack, OpenBankingIncomeQuery, useAddOpenBankingFeedBackMutation } from '@graphql/index';
import { useOpenBankingSteps } from '@provider';
import { Field } from '@spark-web/field';
import {
  EOpenBankingError,
  OPEN_BANKING_INCOME_DETAILS,
  OPEN_BANKING_POLLING_INTERVAL,
  getApplicantTypeAndTrackingIdFromUrl,
} from '@utils';
import OpenBankingIncomeReviewError from './OpenBankingIncomeReviewError';
import { OpenBankingIncomeReviewSuccess } from './OpenBankingIncomeReviewSuccess';
import { BankIncomeSources, useEditToIncomeVerification } from './hooks';

interface OpenBankingIncomeReviewProps {
  isOpenBankingSyncError?: boolean;
  openBankingData?: OpenBankingIncomeQuery['openBankingIncome'];
  getConsentDataLazy: () => void;
  bankIncomeSources: BankIncomeSources;
}

export const OpenBankingIncomeReview = ({
  isOpenBankingSyncError,
  openBankingData,
  getConsentDataLazy,
  bankIncomeSources,
}: OpenBankingIncomeReviewProps) => {
  const { applicantType, trackingId } = getApplicantTypeAndTrackingIdFromUrl();
  const { formState, setValue, reset } = useFormContext<IncomeReviewFieldsType>();
  const { setOpenBankingIncomeSyncState } = useOpenBankingSteps();
  const { openBankingModalState, closeOpenBankingModal, setOpenBankingModalState } = useOpenBankingConsentState();

  const totalMonthlyIncome = openBankingData?.totalMonthlyIncome || 0;
  const incomeReviewConfirmationError = formState?.errors.applicant?.isIncomeReviewConfirmation?.message;
  const handleEditToIncomeVerification = useEditToIncomeVerification();

  // Open Banking Handler
  const handleConsentRequestSuccess = () => {
    reset();
    setOpenBankingIncomeSyncState({ status: 'loading' });
    closeOpenBankingModal();
    // Ensure we successfully receive the consent.received event from Fiskil.
    setTimeout(getConsentDataLazy, OPEN_BANKING_POLLING_INTERVAL);
  };

  const handleConsentRequestError = (errorType?: EOpenBankingError) => {
    closeOpenBankingModal();

    if (errorType === EOpenBankingError.CONSENT_ENDUSER_DENIED) {
      setOpenBankingModalState(OpenBankingModalState.FEEDBACK);
    } else {
      setOpenBankingIncomeSyncState({ status: 'error', errorType });
    }
  };

  const { getOpenBankingConsentUrl, isFetchingOpenBankingConsentDetails } = useLazyOpenBankingConsentUrl({
    applicationId: trackingId,
    onConsentStart: () => setOpenBankingModalState(OpenBankingModalState.CONSENT_IFRAME),
    onConsentSuccess: handleConsentRequestSuccess,
    onConsentError: handleConsentRequestError,
  });
  const { mutate: addOpenBankingFeedBackMutation } = useAddOpenBankingFeedBackMutation();

  const handleConnectOpenBanking = () => {
    datadogRum.addAction('User selected CDR consent again to connect to another bank account');
    getOpenBankingConsentUrl();

    // DEV_MODE: Simulate successful user consent for development purposes
    if (import.meta.env.DEV && process.env.DEV_MODE !== 'staging') {
      setTimeout(() => {
        handleConsentRequestSuccess();
      }, 500);
    }
  };

  const handleSubmitFeedback = (feedback: Array<OpenBankingFeedBack>) => {
    addOpenBankingFeedBackMutation({ feedback });
    closeOpenBankingModal();
  };

  //FYI: The income data will be saved to the missing data in Income Verification step
  useEffect(() => {
    setValue('applicant.usingOpenBanking', true);
    setValue('applicant.income.primary.amount', totalMonthlyIncome ?? 0);
    setValue('applicant.income.primary.period', Period.Monthly);
    setValue('applicant.income.isAdditionalIncome', false);
    setValue('applicant.type', applicantType);
  }, [totalMonthlyIncome, applicantType]);

  return (
    <Field
      label=""
      description={OPEN_BANKING_INCOME_DETAILS.OPEN_BANKING_REVIEW_DESCRIPTION}
      tone={incomeReviewConfirmationError ? 'critical' : undefined}
      message={incomeReviewConfirmationError ? incomeReviewConfirmationError : undefined}
    >
      {isOpenBankingSyncError ? (
        <OpenBankingIncomeReviewError
          onIncomeManualEntry={handleEditToIncomeVerification}
          isOpenBankingConnecting={isFetchingOpenBankingConsentDetails}
          onOpenBankingConnect={() => setOpenBankingModalState(OpenBankingModalState.INSTRUCTION)}
          bankIncomeSources={bankIncomeSources}
        />
      ) : (
        <OpenBankingIncomeReviewSuccess
          openBankingData={openBankingData}
          onIncomeManualEntry={handleEditToIncomeVerification}
          isOpenBankingConnecting={isFetchingOpenBankingConsentDetails}
          onOpenBankingConnect={() => setOpenBankingModalState(OpenBankingModalState.INSTRUCTION)}
          bankIncomeSources={bankIncomeSources}
        />
      )}

      <OpenBankingFeedbackModal
        isOpen={openBankingModalState === OpenBankingModalState.FEEDBACK}
        onSubmit={handleSubmitFeedback}
        onToggle={closeOpenBankingModal}
      />
      <OpenBankingInstructionModal
        isOpen={openBankingModalState === OpenBankingModalState.INSTRUCTION}
        isLoading={isFetchingOpenBankingConsentDetails}
        onToggle={closeOpenBankingModal}
        onConnect={handleConnectOpenBanking}
      />
    </Field>
  );
};
